<script>
import { format, parseISO } from 'date-fns';
import { required, url } from '@vuelidate/validators';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import HelperTextPopup from 'dashboard/components/ui/HelperTextPopup.vue';
import { isValidURL } from '../helper/URLHelper';
import { getRegexp } from 'shared/helpers/Validators';
import { useVuelidate } from '@vuelidate/core';
import { emitter } from 'shared/helpers/mitt';

import NextButton from 'dashboard/components-next/button/Button.vue';

const DATE_FORMAT = 'yyyy-MM-dd';

export default {
  components: {
    MultiselectDropdown,
    HelperTextPopup,
    NextButton,
  },
  props: {
    label: { type: String, required: true },
    description: { type: String, default: '' },
    values: { type: Array, default: () => [] },
    value: { type: [String, Number, Boolean], default: '' },
    showActions: { type: Boolean, default: false },
    attributeType: { type: String, default: 'text' },
    attributeRegex: {
      type: String,
      default: null,
    },
    regexCue: { type: String, default: null },
    attributeKey: { type: String, required: true },
    contactId: { type: Number, default: null },
    allCustomAttributes: { type: Object, default: () => ({}) },
  },
  emits: ['update', 'delete', 'copy'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isEditing: false,
      editedValue: null,
      isExpanded: false,
    };
  },
  computed: {
    displayValue() {
      if (this.isAttributeTypeDate) {
        return this.value
          ? new Date(this.value || new Date()).toLocaleDateString()
          : '---';
      }
      if (this.isAttributeTypeCheckbox) {
        return this.value === 'false' ? false : this.value;
      }
      return this.hasValue ? this.value : '---';
    },
    formattedValue() {
      return this.isAttributeTypeDate
        ? format(this.value ? new Date(this.value) : new Date(), DATE_FORMAT)
        : this.value;
    },
    listOptions() {
      return this.values.map((value, index) => ({
        id: index + 1,
        name: value,
      }));
    },
    selectedItem() {
      const id = this.values.indexOf(this.editedValue) + 1;
      return { id, name: this.editedValue };
    },
    isAttributeTypeCheckbox() {
      return this.attributeType === 'checkbox';
    },
    isAttributeTypeList() {
      return this.attributeType === 'list';
    },
    isAttributeTypeLink() {
      return this.attributeType === 'link';
    },
    isAttributeTypeDate() {
      return this.attributeType === 'date';
    },
    hasValue() {
      return this.value !== null && this.value !== '';
    },
    urlValue() {
      return isValidURL(this.value) ? this.value : '---';
    },
    hrefURL() {
      return isValidURL(this.value) ? this.value : '';
    },
    notAttributeTypeCheckboxAndList() {
      return !this.isAttributeTypeCheckbox && !this.isAttributeTypeList;
    },
    inputType() {
      return this.isAttributeTypeLink ? 'url' : this.attributeType;
    },
    shouldShowErrorMessage() {
      return this.v$.editedValue.$error;
    },
    errorMessage() {
      if (this.v$.editedValue.url) {
        return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_URL');
      }
      if (!this.v$.editedValue.regexValidation) {
        return this.regexCue
          ? this.regexCue
          : this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_INPUT');
      }
      return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.REQUIRED');
    },
  },
  watch: {
    value() {
      this.isEditing = false;
      this.editedValue = this.formattedValue;
    },
    contactId() {
      // Fix to solve validation not resetting when contactId changes in contact page
      this.v$.$reset();
    },
  },

  validations() {
    if (this.isAttributeTypeLink) {
      return {
        editedValue: { required, url },
      };
    }
    return {
      editedValue: {
        required,
        regexValidation: value => {
          return !(
            this.attributeRegex && !getRegexp(this.attributeRegex).test(value)
          );
        },
      },
    };
  },
  mounted() {
    this.editedValue = this.formattedValue;
    emitter.on(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  unmounted() {
    emitter.off(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  methods: {
    onFocusAttribute(focusAttributeKey) {
      if (this.attributeKey === focusAttributeKey) {
        this.onEdit();
      }
    },
    focusInput() {
      if (this.$refs.inputfield) {
        this.$refs.inputfield.focus();
      }
    },
    onClickAway() {
      this.v$.$reset();
      this.isEditing = false;
    },
    autoResize(minPx = 300, maxPx = 600) {
  this.$nextTick(() => {
    const el = this.$refs.inputfield;
    if (!el) return;
    el.style.height = 'auto';
    const target = Math.max(minPx, Math.min(el.scrollHeight, maxPx));
    el.style.height = target + 'px';
  });
},
    onEdit() {
  this.autoResize(300, 600);    this.isEditing = true;
      this.$nextTick(() => {
        this.focusInput();
        this.autoResize(300, 600);
      });
    },
    onUpdateListValue(value) {
      if (value) {
        this.editedValue = value.name;
        this.onUpdate();
      }
    },
isHTMLContent(value) {
  // Detecta se o conteúdo contém tags HTML (especificamente nossos cards)
  return value && typeof value === 'string' && 
         (value.includes('<div style=') || value.includes('<div class='));
},
    onUpdate() {
      const updatedValue =
        this.attributeType === 'date'
          ? parseISO(this.editedValue)
          : this.editedValue;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.isEditing = false;
      this.$emit('update', this.attributeKey, updatedValue);
    },
    onDelete() {
      this.isEditing = false;
      this.v$.$reset();
      this.$emit('delete', this.attributeKey);
    },
    onCopy() {
      this.$emit('copy', this.value);
    },
    copyText(text) {
     navigator.clipboard.writeText(text);
    },
    toggleExpand() {
     this.isExpanded = !this.isExpanded;
    },
    isVisibleByDependency(attributeKey) {
  const dependencyMap = {
    produto: 'mostrar_produto',
    pedido: 'mostrar_pedido',
    envio: 'mostrar_envio',
    reclamacao: 'mostrar_reclamacao',
    devolucao: 'mostrar_devolucao',
  };

    const dependeDe = dependencyMap[attributeKey];
  if (!dependeDe) return true;

  const valor = this.allCustomAttributes[dependeDe];

  // Agora só mostra se o checkbox NÃO estiver marcado (false ou undefined)
  return valor !== true;
    },
  },
};
</script>

<template>
  <div
  class="px-4 py-1"
  v-if="isVisibleByDependency(attributeKey) &&
         !['bloqueado_envio', 'vence_hoje', 'reembolsar', 'devolver', 'mediacao', 'reembolsarvalor', 'webhook', 'bloqueado_msg', 'aviso_msg', 'bloqueado_aviso', 'mostrar_envio', 'mostrar_devolucao', 'mostrar_reclamacao', 'mostrar_pedido', 'mostrar_produto', 'devolucao_ok', 'devolucao_falha', 'custo_devolucao'].includes(attributeKey)"
>

    <div class="flex items-center mb-0">
      <div class="flex items-center w-full">
        <!-- Checkbox -->
        <div v-if="isAttributeTypeCheckbox" class="flex items-center">
          <input
            v-model="editedValue"
            class="!my-0 ltr:mr-2 ltr:ml-0 rtl:mr-0 rtl:ml-2"
            type="checkbox"
            @change="onUpdate"
          />
        </div>

        <!-- Bloco clicável (exceto botões) -->
        <div class="flex w-full justify-between items-center">
          <div
            class="flex-1 w-full select-none py-1"
            @click="toggleExpand"
          >
            <span
              class="inline-flex gap-1.5 items-start font-bold whitespace-nowrap text-sm mb-0 text-n-slate-12"
            >
              {{ label }}
            </span>
          </div>

          <!-- Botões de ação -->
          <div class="flex items-center gap-1 ml-2">
            <NextButton
              v-if="showActions && hasValue"
              v-tooltip.left="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
              slate
              sm
              link
              icon="i-lucide-trash-2"
              @click.stop="onDelete"
            />
            <NextButton
              v-if="showActions"
              xs
              slate
              ghost
              icon="i-lucide-pen"
              @click.stop="onEdit"
            />
            <NextButton
              xs
              slate
              ghost
              :icon="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
              @click.stop="toggleExpand"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="notAttributeTypeCheckboxAndList">
      <div v-if="isEditing" v-on-clickaway="onClickAway">
  <div class="flex items-start w-full mb-2">
    <textarea
  ref="inputfield"
  v-model="editedValue"
  rows="15"
  class="w-full !h-auto !min-h-[300px] !max-h-[600px] !leading-5 !text-sm !mb-0 resize-y
         ltr:!rounded-r-none rtl:!rounded-l-none"
  :class="{ error: v$.editedValue.$error }"
  autofocus
  @input="autoResize(300, 600)"
  @blur="v$.editedValue.$touch"
  @keydown.ctrl.enter.prevent="onUpdate"
  @keydown.meta.enter.prevent="onUpdate"
></textarea>
    <div>
      <NextButton
        sm
        icon="i-lucide-check"
        class="ltr:rounded-l-none rtl:rounded-r-none h-[34px] ml-2"
        @click="onUpdate"
      />
    </div>
  </div>

  <span
    v-if="shouldShowErrorMessage"
    class="block w-full -mt-px text-sm font-normal text-n-ruby-11"
  >
    {{ errorMessage }}
  </span>
</div>
      <div
        v-show="!isEditing"
        class="flex"
        :class="{ 'is-editable': showActions }"
      >
        <a
          v-if="isAttributeTypeLink"
          :href="hrefURL"
          target="_blank"
          rel="noopener noreferrer"
          class="group-hover:bg-n-slate-3 group-hover:dark:bg-n-solid-3 inline-block rounded-sm mb-0 break-all py-0.5 px-1"
        >
          {{ urlValue }}
        </a>
<!-- Quebra em múltiplas linhas formatadas -->
<div
   v-show="isExpanded"
   class="group-hover:bg-n-slate-3 group-hover:dark:bg-n-solid-3 block w-full rounded-sm mb-0 break-words py-0.5 px-1"
>
  <!-- Se for conteúdo HTML (pedidos/dados formatados), renderiza como HTML -->
  <div
    v-if="isHTMLContent(displayValue)"
    v-html="displayValue"
    class="custom-html-content"
  ></div>
  
  <!-- Caso contrário, mantém o comportamento padrão -->
  <div v-else>
    <div
      v-for="(line, index) in displayValue.split('\n')"
      :key="index"
      class="mb-4 atributo-item cursor-pointer"
      @click="copyText(line.includes(':') ? line.substring(line.indexOf(':') + 1).trim() : line)"
    >
      <template v-if="line.includes(':')">
        <strong>{{ line.split(':')[0] }}:</strong><br />
        <span>{{ line.substring(line.indexOf(':') + 1).trim() }}</span>
      </template>
      <template v-else>
        {{ line }}
      </template>
    </div>
  </div>
</div>
        <div
          class="flex items-center max-w-[2rem] gap-1 ml-1 rtl:mr-1 rtl:ml-0"
        >
        </div>
      </div>
    </div>
    <div v-if="isAttributeTypeList">
      <MultiselectDropdown
        :options="listOptions"
        :selected-item="selectedItem"
        :has-thumbnail="false"
        :multiselector-placeholder="
          $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.PLACEHOLDER')
        "
        :no-search-result="
          $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.NO_RESULT')
        "
        :input-placeholder="
          $t(
            'CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.SEARCH_INPUT_PLACEHOLDER'
          )
        "
        @select="onUpdateListValue"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .selector-wrap {
    @apply m-0 top-1;

    .selector-name {
      @apply ml-0;
    }
  }
  .name {
    @apply ml-0;
  }
}

/* Estilos para os cards HTML customizados */
.custom-html-content {
  /* Remove padding/margin extras */
  & > div {
    margin: 0;
  }
  
  /* Garante que os cards fiquem responsivos */
  & * {
    max-width: 100%;
  }
  
  /* Estilos para links dentro dos cards */
  a {
    color: #2196F3;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
